import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Login.css';
import { useAuth } from '../contexts/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebase';
import { db, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [nickname, setNickname] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const initializeProfile = async (nextNickname) => {
    const updateProfileFn = httpsCallable(functions, 'updateProfile', { timeout: 30000 });
    await updateProfileFn({
      nickname: nextNickname.trim(),
      bio: '',
      climbingHistory: '',
      favoriteMountains: '',
      nearestStation: '',
      hasCar: false,
      gender: '',
      ageGroup: '',
      residencePrefecture: '',
      monthlyFrequency: '',
      hikingStyle: [],
      pace: '',
      fitnessLevel: '',
      altitudeExperience: [],
      technicalExperience: [],
      ownedGear: [],
      yamapLink: '',
      yamarecoLink: '',
      twitterLink: '',
      youtubeLink: '',
      instagramLink: '',
      photoURL: '',
      climbingExperience: {},
      mountainInsurance: '',
      cocoheriNumber: '',
      ageConfirmed: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError('パスワードが一致しません。');
      return;
    }

    if (!agreed) {
      setError('利用規約に同意してください。');
      return;
    }

    if (!ageConfirmed) {
      setError('18歳以上であることを確認してください。');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password);
      await initializeProfile(nickname);
      await sendEmailVerification(auth.currentUser);
      navigate('/verify-email');
    } catch (error) {
      console.error('アカウント作成エラー:', error);
      let errorMessage = 'アカウントの作成に失敗しました。';
      
      if (error.code === 'auth/email-already-in-use') {
        try {
          await login(email, password);
          await initializeProfile(nickname);
          navigate('/profile/edit');
          return;
        } catch (recoveryError) {
          console.error('既存アカウント復旧に失敗しました:', recoveryError);
          errorMessage = 'このメールアドレスは既に使用されています。すでに登録済みの可能性があります。ログインをお試しください。';
        }
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '有効なメールアドレスを入力してください。';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'パスワードが弱すぎます。より強力なパスワードを設定してください。';
      } else if (error.code === 'functions/internal' || error.code === 'functions/unavailable') {
        errorMessage = 'アカウントは作成された可能性があります。ログイン後にプロフィール設定を完了してください。';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください。';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    if (!ageConfirmed) {
      setError('18歳以上であることを確認してください。');
      return;
    }
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      
      // ユーザーが既に存在するか確認
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (userDoc.exists()) {
        // 既存ユーザーは通常通りログイン
        navigate('/');
      } else {
        // 新規ユーザーは追加情報入力画面へ
        navigate(`/google-signup?email=${encodeURIComponent(result.user.email)}&displayName=${encodeURIComponent(result.user.displayName || '')}`);
      }
    } catch (error) {
      console.error('Google新規登録に失敗しました:', error);
      setError('Googleアカウントでの登録に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="card auth-card">
        <h2 className="text-center mb-4">新規登録</h2>
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">メールアドレス</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label className="form-label required">ニックネーム <span className="required-mark">*</span></label>
            <input
              type="text"
              className="form-control"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="ニックネームを入力してください"
              required
              maxLength="20"
            />
            <small className="form-text text-muted">
              最大20文字。山のぼろ内での表示名になります。
            </small>
          </div>
          
          <div className="form-group">
            <label className="form-label">パスワード</label>
            <input
              type="password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength="6"
            />
          </div>
          
          <div className="form-group">
            <label className="form-label">パスワード（確認）</label>
            <input
              type="password"
              className="form-control"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              minLength="6"
            />
          </div>
          
          <div className="form-group">
            <label className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                required
              />
              <span className="form-check-label">
                <a href="https://yamano-media.com/blog/terms-of-service/" target="_blank" rel="noopener noreferrer">利用規約</a>・<a href="https://yamano-media.com/blog/policy/" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>に同意します <span className="required-mark">*</span>
              </span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                required
              />
              <span className="form-check-label">
                18歳以上です <span className="required-mark">*</span>
              </span>
            </label>
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary w-100"
            disabled={loading}
          >
            {loading ? '登録中...' : '登録'}
          </button>
        </form>

        <div className="divider">
          <span>または</span>
        </div>

        <button 
          className="btn btn-google w-100"
          onClick={handleGoogleSignUp}
          disabled={loading}
        >
          <span className="google-icon">🔗</span>
          {loading ? '登録中...' : 'Googleで登録'}
        </button>
        
        <div className="auth-links mt-4">
          <div className="text-center">
            <Link to="/login">ログインはこちら</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup;
